// src/contexts/LanguageContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import i18n from '../i18n/config';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Language code to locale mapping for date formatting
const languageToLocale = {
  'en': 'en-US',
  'es': 'es-ES',
  'fr': 'fr-FR',
  'de': 'de-DE',
  'zh': 'zh-CN',
  'sw': 'sw-KE', // Swahili
  'am': 'am-ET', // Amharic
  'ha': 'ha-NG', // Hausa
  'yo': 'yo-NG', // Yoruba
  'ig': 'ig-NG', // Igbo
  'zu': 'zu-ZA', // Zulu
  'xh': 'xh-ZA', // Xhosa
  'af': 'af-ZA', // Afrikaans
  'ar': 'ar-SA', // Arabic
  'so': 'so-SO', // Somali
  'rw': 'rw-RW', // Kinyarwanda
  'ak': 'ak-GH', // Akan
  'ff': 'ff-SN', // Fulfulde
  'ln': 'ln-CD', // Lingala
  'mg': 'mg-MG', // Malagasy
  'om': 'om-ET', // Oromo
  'ti': 'ti-ER', // Tigrinya
  'tn': 'tn-BW', // Tswana
  've': 've-ZA', // Venda
  'wo': 'wo-SN', // Wolof
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Try to get from localStorage first
    const stored = localStorage.getItem('userLanguage');
    if (stored) return stored;
    
    // Try to get from stored user data
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        if (user?.preferences?.language) {
          return user.preferences.language;
        }
      }
    } catch (e) {
      // Ignore parse errors
    }
    
    // Default to English
    return 'en';
  });
  const [locale, setLocale] = useState(() => languageToLocale[language] || 'en-US');

  // Update locale and i18n when language changes
  useEffect(() => {
    const newLocale = languageToLocale[language] || 'en-US';
    setLocale(newLocale);
    localStorage.setItem('userLanguage', language);
    
    // Always update i18next language to ensure it's in sync
    const updateI18n = async () => {
      try {
        console.log('LanguageContext: Changing language to', language);
        // Change language and wait for it to complete
        await i18n.changeLanguage(language);
        console.log('LanguageContext: i18n language changed to', i18n.language);
        
        // Force a re-render by dispatching a custom event
        window.dispatchEvent(new CustomEvent('languageChanged', { 
          detail: { language } 
        }));
      } catch (error) {
        console.error('Error changing language:', error);
      }
    };
    updateI18n();
    
    // Update document language attribute for accessibility
    document.documentElement.lang = language;
  }, [language]);

  // Load language from user settings on mount
  useEffect(() => {
    const loadUserLanguage = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await api.get('/users/settings');
        if (response.data?.preferences?.language) {
          setLanguage(response.data.preferences.language);
        }
      } catch (error) {
        // User not authenticated or error loading settings
        // Use stored language or default
        const stored = localStorage.getItem('userLanguage');
        if (stored) {
          setLanguage(stored);
        }
      }
    };
    
    loadUserLanguage();
  }, []);

  // Format date according to selected language
  const formatDate = (date, options = {}) => {
    if (!date) return '';
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleDateString(locale, options);
  };

  // Format date and time according to selected language
  const formatDateTime = (date, options = {}) => {
    if (!date) return '';
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleString(locale, options);
  };

  // Format time according to selected language
  const formatTime = (date, options = {}) => {
    if (!date) return '';
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleTimeString(locale, options);
  };

  const updateLanguage = async (newLanguage) => {
    // Update localStorage immediately
    localStorage.setItem('userLanguage', newLanguage);
    
    // Update i18n language and wait for it
    try {
      await i18n.changeLanguage(newLanguage);
    } catch (error) {
      console.error('Error changing i18n language:', error);
    }
    
    // Update state to trigger re-renders
    setLanguage(newLanguage);
    
    // Dispatch event for any components listening
    window.dispatchEvent(new CustomEvent('languageChanged', { 
      detail: { language: newLanguage } 
    }));
  };

  const value = {
    language,
    locale,
    setLanguage: updateLanguage,
    formatDate,
    formatDateTime,
    formatTime,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;

